import { useState, useEffect, useCallback } from 'react';
import { ApiFout, api, metHerhaalAdvies } from '@/lib/api';
import type { AppInstellingen, AzureDeployment, TokenVerbruik } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Sparkles, RefreshCw, FlaskConical, FileText, Copy, ChevronDown, ChevronRight, RotateCcw, ShieldAlert, ShieldCheck, History } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { AiTestSuitePanel } from './AiTestSuitePanel';

export function AiSettingsCard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTestingAzure, setIsTestingAzure] = useState(false);
  const [isLoadingDeployments, setIsLoadingDeployments] = useState(false);

  const [preferredModel, setPreferredModel] = useState('auto');
  const [azureEndpoint, setAzureEndpoint] = useState('');
  const [azureModel, setAzureModel] = useState('');
  /**
   * Over de sleutel: alleen of hij er is, waar hij staat en de laatste vier tekens.
   *
   * Er is hier geen invoerveld meer. Het scherm haalde de volledige sleutel op om
   * hem in een `type="password"`-veld te zetten -- dat verbergt hem voor een
   * schouderblik maar niet voor de ontwikkelaarsconsole, en hij stond in het
   * netwerkverkeer. Roteren gebeurt vanaf fase 5 in Key Vault.
   */
  const [sleutelStatus, setSleutelStatus] = useState<{
    gezet: boolean;
    bron: AppInstellingen['azureApiKeyBron'];
    hint: string | null;
  }>({ gezet: false, bron: 'ontbreekt', hint: null });
  const [azureDeployments, setAzureDeployments] = useState<AzureDeployment[]>([]);
  const [testTranscript, setTestTranscript] = useState('');
  /**
   * De uitkomst van de proefaanroep. Alleen wat dit scherm ervan toont: de vier
   * SOEP-velden, het model en het tokenverbruik.
   */
  const [testResult, setTestResult] = useState<{
    soep: { subjective: string; objective: string; evaluation: string; plan: string };
    model: string;
    usage: TokenVerbruik;
  } | null>(null);
  const [icpcSuggestions, setIcpcSuggestions] = useState<{ code: string; description: string }[]>([]);
  const [isTestingIcpc, setIsTestingIcpc] = useState(false);
  const [icpcError, setIcpcError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showTestSuite, setShowTestSuite] = useState(false);
  const [promptMultiple, setPromptMultiple] = useState(false);

  // Bewerkbare admin-prompts (leeg = ingebouwde standaard).
  const [promptSoep, setPromptSoep] = useState('');
  const [promptIcpc, setPromptIcpc] = useState('');
  const [promptTranslate, setPromptTranslate] = useState('');
  const [promptSoepSingle, setPromptSoepSingle] = useState('');
  const [promptSoepMulti, setPromptSoepMulti] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  /**
   * Het promptvoorbeeld komt van de server.
   *
   * Dit werd in de browser samengesteld, met een eigen kopie van de opbouwlogica in
   * default-prompts.ts: 191 regels naast de 246 in apps/api/src/lib/prompts.ts. Twee
   * plekken die hetzelfde doen lopen uit elkaar, en dan toont het voorbeeld iets
   * anders dan het model krijgt.
   */
  const [preview, setPreview] = useState<{ single: string; multiple: string }>({
    single: '',
    multiple: '',
  });
  const [standaarden, setStandaarden] = useState<{
    soepSystem: string;
    soepSingleProblem: string;
    soepMultiProblem: string;
    icpcSystem: string;
    translateSystem: string;
  } | null>(null);

  const currentPrompt = promptMultiple ? preview.multiple : preview.single;

  // Vertraagd, want dit loopt bij elke toetsaanslag in de promptvelden.
  useEffect(() => {
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const antwoord = await api.post<{
            preview: { single: string; multiple: string };
            defaults: {
              soepSystem: string;
              soepSingleProblem: string;
              soepMultiProblem: string;
              icpcSystem: string;
              translateSystem: string;
            };
          }>('/admin/settings/prompt-preview', {
            promptSoepSystem: promptSoep,
            promptSoepSingleProblem: promptSoepSingle,
            promptSoepMultiProblem: promptSoepMulti,
          });
          setPreview(antwoord.preview);
          setStandaarden(antwoord.defaults);
        } catch {
          // Een mislukt voorbeeld is geen reden om het scherm te blokkeren.
        }
      })();
    }, 400);

    return () => clearTimeout(timer);
  }, [promptSoep, promptSoepSingle, promptSoepMulti]);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const { settings } = await api.get<{ settings: AppInstellingen | null }>('/admin/settings');
      if (settings) {
        setPreferredModel(settings.preferredModel ?? 'auto');
        setAzureEndpoint(settings.azureEndpoint ?? '');
        setAzureModel(settings.azureModel ?? '');
        setSleutelStatus({
          gezet: settings.azureApiKeySet,
          bron: settings.azureApiKeyBron,
          hint: settings.azureApiKeyHint,
        });
        setPromptSoep(settings.promptSoepSystem ?? '');
        setPromptIcpc(settings.promptIcpcSystem ?? '');
        setPromptTranslate(settings.promptTranslateSystem ?? '');
        setPromptSoepSingle(settings.promptSoepSingleProblem ?? '');
        setPromptSoepMulti(settings.promptSoepMultiProblem ?? '');
      }
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de AI-instellingen niet laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Geen azureApiKey in deze aanroep. De route weigert dat veld sinds fase 5 met
      // een 400: een secret dat via een formulier binnenkomt staat daarna in een
      // tabel die een beheerroute kan lezen, en dat is hoe deze sleutel in de
      // browser terechtkwam.
      await api.patch('/admin/settings', {
        aiProvider: 'azure',
        preferredModel,
        azureEndpoint: azureEndpoint.trim(),
        azureModel: azureModel.trim(),
        promptSoepSystem: promptSoep,
        promptIcpcSystem: promptIcpc,
        promptTranslateSystem: promptTranslate,
        promptSoepSingleProblem: promptSoepSingle,
        promptSoepMultiProblem: promptSoepMulti,
      });
      toast.success('AI-instellingen opgeslagen');
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij opslaan AI-instellingen');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchAzureDeployments = async () => {
    setIsLoadingDeployments(true);
    try {
      // Endpoint en sleutel gaan niet meer mee. In de bron kwamen die uit de request
      // body, waarmee dit endpoint elke URL kon aanroepen die de client opgaf -- een
      // manier om de server als proxy naar een willekeurige host te gebruiken.
      const { deployments } = await api.get<{ deployments: AzureDeployment[] }>(
        '/admin/azure/deployments',
      );
      setAzureDeployments(deployments);
      if (deployments.length > 0) {
        toast.success(`${deployments.length} deployment(s) gevonden`);
      } else {
        toast.warning('Geen deployments gevonden');
      }
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij het ophalen van deployments');
    } finally {
      setIsLoadingDeployments(false);
    }
  };

  const handleTestAzure = async () => {
    setIsTestingAzure(true);
    setTestResult(null);
    setIcpcSuggestions([]);
    setIcpcError(null);

    try {
      /**
       * Eén aanroep naar POST /api/admin/ai/test.
       *
       * De vorige versie riep Azure rechtstreeks aan met de sleutel uit het
       * formulier, en logde het verbruik door een praktijk "Intern Admin" aan te
       * maken, daar een gebruiker bij te maken, het access_token van die praktijk
       * op te halen en met dat token log-usage aan te roepen. Vier queries en een
       * verzonnen praktijk in de klantenlijst, voor één regel in het logboek.
       */
      const antwoord = await api.post<{
        ok: boolean;
        result: { problems?: Array<Record<string, string>> } & Record<string, unknown>;
        model: string;
        usage: { promptTokens: number; completionTokens: number };
      }>('/admin/ai/test', { transcript: testTranscript.trim() });

      const eerste = antwoord.result.problems?.[0] ?? (antwoord.result as Record<string, string>);
      const soep = {
        subjective: eerste.subjective ?? '',
        objective: eerste.objective ?? '',
        evaluation: eerste.evaluation ?? '',
        plan: eerste.plan ?? '',
      };

      setTestResult({ soep, model: antwoord.model, usage: antwoord.usage });
      toast.success('Azure AI verbinding succesvol');

      // Ook de ICPC-suggestie testen, met de evaluatietekst die er net uitkwam.
      const evalText = soep.evaluation || soep.subjective || '';
      if (evalText.trim().length >= 3) {
        setIsTestingIcpc(true);
        try {
          const { suggestions } = await api.post<{ suggestions: { code: string; description: string }[] }>(
            '/admin/ai/test-icpc',
            { evaluationText: evalText },
          );
          setIcpcSuggestions(suggestions);
          if (suggestions.length > 0) {
            toast.success(`${suggestions.length} ICPC-suggestie(s) gevonden`);
          } else {
            toast.warning('Geen ICPC-suggesties gevonden');
          }
        } catch (icpcFout) {
          const melding = metHerhaalAdvies(
            icpcFout instanceof ApiFout ? icpcFout.message : 'ICPC-suggestie mislukt',
            icpcFout,
          );
          setIcpcError(melding);
          toast.error(`ICPC-test mislukt: ${melding}`);
        } finally {
          setIsTestingIcpc(false);
        }
      }
    } catch (fout) {
      toast.error(
        metHerhaalAdvies(fout instanceof ApiFout ? fout.message : 'Azure-test mislukt', fout),
      );
    } finally {
      setIsTestingAzure(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          AI Instellingen
        </CardTitle>
        <CardDescription>
          Globale AI-configuratie voor alle praktijken en gebruikers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Azure AI Foundry configuratie */}
        <div className="space-y-4 rounded-md border border-border p-4 bg-muted/30">
            <h4 className="text-sm font-semibold">Azure AI Foundry configuratie</h4>
            <div className="space-y-2">
              <Label>Endpoint URL</Label>
              <Input
                value={azureEndpoint}
                onChange={(e) => setAzureEndpoint(e.target.value)}
                placeholder="https://<resource>.services.ai.azure.com"
              />
              <p className="text-xs text-muted-foreground">
                Het Azure AI Foundry endpoint (zonder /chat/completions pad).
              </p>
            </div>
            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                {sleutelStatus.gezet ? (
                  <>
                    <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-mono">{sleutelStatus.hint}</span>
                    <span className="text-xs text-muted-foreground">
                      {sleutelStatus.bron === 'keyvault' ? 'uit Key Vault' : 'uit de database'}
                    </span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="h-4 w-4 text-destructive shrink-0" />
                    <span className="text-destructive">Niet ingesteld</span>
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                De sleutel staat in Key Vault en is hier niet in te voeren of te lezen. Roteren gaat
                met <code className="text-[11px]">infra/scripts/set-secrets.sh</code>; de app pikt een
                nieuwe versie binnen 24 uur op, of onmiddellijk na een herstart.
                {sleutelStatus.bron === 'database' && (
                  <>
                    {' '}
                    <span className="text-destructive">
                      Deze omgeving gebruikt nog de waarde uit de database. In productie weigert de API
                      dat.
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Model / Deployment naam</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    list="azure-model-options-app"
                    value={azureModel}
                    onChange={(e) => setAzureModel(e.target.value)}
                    placeholder="Bijv. gpt-4.1 of mijn-eigen-deployment"
                  />
                  <datalist id="azure-model-options-app">
                    {azureDeployments.length > 0 ? (
                      azureDeployments.map((d) => (
                        <option key={d.id} value={d.id}>{d.id}{d.model ? ` (${d.model})` : ''}</option>
                      ))
                    ) : (
                      <>
                        <option value="gpt-4o" />
                        <option value="gpt-4o-mini" />
                        <option value="gpt-4.1" />
                        <option value="gpt-4.1-mini" />
                        <option value="gpt-4.1-nano" />
                        <option value="o4-mini" />
                        <option value="o3" />
                        <option value="o3-mini" />
                      </>
                    )}
                  </datalist>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  disabled={isLoadingDeployments || !azureEndpoint.trim()}
                  onClick={fetchAzureDeployments}
                  className="whitespace-nowrap"
                >
                  {isLoadingDeployments ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
              {azureDeployments.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {azureDeployments.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setAzureModel(d.id)}
                      className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${azureModel === d.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:bg-accent'}`}
                    >
                      {d.id}{d.model ? ` (${d.model})` : ''}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Klik op 🔄 om beschikbare deployments op te halen, of vul handmatig de exacte naam in.
              </p>
            </div>
            <div className="space-y-2 border-t pt-3 mt-3">
              <Label className="text-sm font-medium flex items-center gap-1">
                <FlaskConical className="h-4 w-4" />
                Test SOEP-generatie
              </Label>
              <div className="flex gap-2 items-start">
                <Textarea
                  placeholder="Plak hier een transcriptie om te testen... (leeg = standaard testzin)"
                  value={testTranscript}
                  onChange={(e) => setTestTranscript(e.target.value)}
                  rows={3}
                  className="text-sm flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  title="Genereer voorbeeldgesprek"
                  onClick={() => setTestTranscript(`[Arts]: Goedemorgen, wat kan ik voor u doen?\n[Patiënt]: Ik heb al een paar dagen last van hoofdpijn, vooral achter mijn ogen. En ik voel me ook een beetje duizelig.\n[Arts]: Hoe lang heeft u hier precies last van?\n[Patiënt]: Sinds maandag, dus nu vier dagen.\n[Arts]: Heeft u ook last van misselijkheid of braken?\n[Patiënt]: Nee, alleen de hoofdpijn en duizeligheid.\n[Arts]: Gebruikt u medicijnen?\n[Patiënt]: Alleen af en toe een paracetamol, maar dat helpt niet echt.\n[Arts]: Ik ga uw bloeddruk even meten. Die is 145 over 92, dat is wat aan de hoge kant. Ik wil graag bloedonderzoek laten doen om een paar dingen uit te sluiten.\n[Patiënt]: Oké, dat is goed.\n[Arts]: Ik denk dat het spanningshoofdpijn kan zijn, mogelijk in combinatie met licht verhoogde bloeddruk. Ik schrijf ibuprofen voor en we plannen een controleafspraak over twee weken.`)}
                  className="mt-1 shrink-0"
                >
                  📝 Voorbeeld
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={isTestingAzure || !azureEndpoint.trim()}
                onClick={handleTestAzure}
              >
                {isTestingAzure ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Testen...</> : '🔌 Test verbinding & SOEP'}
              </Button>
              {testResult?.soep && (
                <div className="rounded-md border bg-muted/30 p-3 space-y-1 text-sm">
                  <p><span className="font-semibold">S:</span> {testResult.soep.subjective || <span className="text-muted-foreground italic">leeg</span>}</p>
                  <p><span className="font-semibold">O:</span> {testResult.soep.objective || <span className="text-muted-foreground italic">leeg</span>}</p>
                  <p><span className="font-semibold">E:</span> {testResult.soep.evaluation || <span className="text-muted-foreground italic">leeg</span>}</p>
                  <p><span className="font-semibold">P:</span> {testResult.soep.plan || <span className="text-muted-foreground italic">leeg</span>}</p>
                  {testResult.usage && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Model: {testResult.model} | Tokens: {testResult.usage.promptTokens} in /{' '}
                      {testResult.usage.completionTokens} out
                    </p>
                  )}
                </div>
              )}
              {/* ICPC suggestie resultaten */}
              {isTestingIcpc && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  ICPC suggesties ophalen...
                </div>
              )}
              {icpcError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                  ⚠️ ICPC suggestie fout: {icpcError}
                </div>
              )}
              {icpcSuggestions.length > 0 && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                  <p className="font-semibold text-xs uppercase tracking-wide text-muted-foreground mb-1">ICPC Suggesties</p>
                  {icpcSuggestions.map((s) => (
                    <p key={s.code}>
                      <span className="font-mono font-semibold text-primary">{s.code}</span>{' '}
                      <span className="text-muted-foreground">—</span> {s.description}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

        {/* Testset: bewaarde transcripties en de vergelijking tussen runs */}
        <div className="space-y-2 rounded-md border border-border p-4 bg-muted/30">
          <button
            type="button"
            onClick={() => setShowTestSuite(!showTestSuite)}
            className="flex items-center gap-2 text-sm font-semibold w-full text-left"
          >
            {showTestSuite ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <History className="h-4 w-4" />
            Testtranscripties &amp; vergelijken met eerdere runs
          </button>
          {showTestSuite && <AiTestSuitePanel />}
        </div>

        {/* SOEP Prompt Preview */}
        <div className="space-y-2 rounded-md border border-border p-4 bg-muted/30">
          <button
            type="button"
            onClick={() => setShowPrompt(!showPrompt)}
            className="flex items-center gap-2 text-sm font-semibold w-full text-left"
          >
            {showPrompt ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <FileText className="h-4 w-4" />
            Ingebouwde SOEP-prompt (bewerkbaar — exact verzonden naar LLM)
          </button>
          {showPrompt && (
            <div className="space-y-2 pt-2">
              <p className="text-xs text-muted-foreground">
                Dit is de basis system prompt. Je kunt hem hier direct aanpassen — leeg = ingebouwde standaard. Praktijk/gebruiker-specifieke onderdelen (herkenningszinnen, rol, specialisaties, verslagstijl, persoonlijk profiel) worden dynamisch ingevoegd via de placeholders <code className="text-[10px]">{'{{SPEAKER_INSTRUCTIONS}}'}</code>, <code className="text-[10px]">{'{{PROVIDER_PROFILE}}'}</code> en <code className="text-[10px]">{'{{MULTI_PROBLEM_INSTRUCTIONS}}'}</code>. De preview hieronder toont de volledig samengestelde prompt.
              </p>
              <div className="flex gap-2 items-center">
                <Select value={promptMultiple ? 'multi' : 'single'} onValueChange={(v) => setPromptMultiple(v === 'multi')}>
                  <SelectTrigger className="w-[260px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Enkelvoudig SOEP-verslag</SelectItem>
                    <SelectItem value="multi">Meerdere problemen toegestaan</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(currentPrompt);
                    toast.success('Prompt gekopieerd');
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Kopieer
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => standaarden && setPromptSoep(standaarden.soepSystem)}
                  title="Herstel standaard"
                >
                  <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                </Button>
              </div>
              <Label className="text-xs font-medium pt-1">Basis-template (bewerkbaar)</Label>
              <Textarea
                value={promptSoep || standaarden?.soepSystem || ''}
                onChange={(e) => setPromptSoep(e.target.value)}
                rows={16}
                className="text-xs font-mono"
                placeholder="Leeg = ingebouwde standaard gebruiken"
              />
              <Label className="text-xs font-medium pt-2">Volledig samengestelde prompt (preview)</Label>
              <Textarea
                value={currentPrompt}
                readOnly
                rows={14}
                className="text-xs font-mono"
              />
            </div>
          )}
        </div>

        {/* Bewerkbare AI-prompts */}
        <div className="space-y-2 rounded-md border border-border p-4 bg-muted/30">
          <button
            type="button"
            onClick={() => setShowPromptEditor(!showPromptEditor)}
            className="flex items-center gap-2 text-sm font-semibold w-full text-left"
          >
            {showPromptEditor ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Sparkles className="h-4 w-4" />
            AI-prompts aanpassen
          </button>
          {showPromptEditor && (
            <div className="space-y-6 pt-3">
              <p className="text-xs text-muted-foreground">
                Deze prompts sturen de AI aan. Laat een veld leeg om de ingebouwde standaard te gebruiken.
                Wijzigingen gelden voor <strong>alle praktijken</strong> zodra je op “Opslaan” klikt.
              </p>

              {/* SOEP */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">SOEP-generatie (system prompt)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => standaarden && setPromptSoep(standaarden.soepSystem)}
                    title="Herstel standaard"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ondersteunde placeholders:{' '}
                  <code className="text-[10px]">{'{{SPEAKER_INSTRUCTIONS}}'}</code>,{' '}
                  <code className="text-[10px]">{'{{PROVIDER_PROFILE}}'}</code>,{' '}
                  <code className="text-[10px]">{'{{MULTI_PROBLEM_INSTRUCTIONS}}'}</code>. Zonder placeholders worden deze automatisch achteraan toegevoegd.
                </p>
                <Textarea
                  value={promptSoep}
                  onChange={(e) => setPromptSoep(e.target.value)}
                  rows={14}
                  placeholder="Leeg = ingebouwde standaard gebruiken"
                  className="text-xs font-mono"
                />
              </div>

              {/* SOEP – Enkelvoudig */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">SOEP – Enkelvoudig contact (instructie-blok)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => standaarden && setPromptSoepSingle(standaarden.soepSingleProblem)}
                    title="Herstel standaard"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Vervangt de placeholder <code className="text-[10px]">{'{{MULTI_PROBLEM_INSTRUCTIONS}}'}</code> wanneer de zorgverlener kiest voor één SOEP-verslag.
                </p>
                <Textarea
                  value={promptSoepSingle}
                  onChange={(e) => setPromptSoepSingle(e.target.value)}
                  rows={6}
                  placeholder="Leeg = ingebouwde standaard gebruiken"
                  className="text-xs font-mono"
                />
              </div>

              {/* SOEP – Meervoudig */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">SOEP – Meervoudig contact (instructie-blok)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => standaarden && setPromptSoepMulti(standaarden.soepMultiProblem)}
                    title="Herstel standaard"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Vervangt de placeholder <code className="text-[10px]">{'{{MULTI_PROBLEM_INSTRUCTIONS}}'}</code> wanneer de zorgverlener kiest voor meerdere SOEP-verslagen per consult.
                </p>
                <Textarea
                  value={promptSoepMulti}
                  onChange={(e) => setPromptSoepMulti(e.target.value)}
                  rows={6}
                  placeholder="Leeg = ingebouwde standaard gebruiken"
                  className="text-xs font-mono"
                />
              </div>

              {/* ICPC */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">ICPC-suggestie (system prompt)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => standaarden && setPromptIcpc(standaarden.icpcSystem)}
                    title="Herstel standaard"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                  </Button>
                </div>
                <Textarea
                  value={promptIcpc}
                  onChange={(e) => setPromptIcpc(e.target.value)}
                  rows={8}
                  placeholder="Leeg = ingebouwde standaard gebruiken"
                  className="text-xs font-mono"
                />
              </div>

              {/* Vertaling */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Vertaling patiëntchat (system prompt)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => standaarden && setPromptTranslate(standaarden.translateSystem)}
                    title="Herstel standaard"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Standaard laden
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Placeholder: <code className="text-[10px]">{'{{TARGET_LANGUAGE}}'}</code> wordt vervangen door de doeltaal.
                </p>
                <Textarea
                  value={promptTranslate}
                  onChange={(e) => setPromptTranslate(e.target.value)}
                  rows={4}
                  placeholder="Leeg = ingebouwde standaard gebruiken"
                  className="text-xs font-mono"
                />
              </div>
            </div>
          )}
        </div>

        <Button onClick={handleSave} className="w-full" disabled={isSaving}>
          {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'AI Instellingen Opslaan'}
        </Button>
      </CardContent>
    </Card>
  );
}
